import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useCallback, useMemo, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { getToken } from 'src/utils/auth';
import { postFormDataAPI } from 'src/utils/api';
import { getToday } from 'src/common/helper';
import { SpecialZoomLevel, Viewer, Worker } from '@react-pdf-viewer/core';
import QrScanner from 'qr-scanner';
import BtnGradient from 'src/components/common/button/BtnGradient';
import BtnOutlineGradient from 'src/components/common/button/BtnOutlineGradient';

import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';
import * as pdfjsLib from 'pdfjs-dist';
import 'pdfjs-dist/build/pdf.worker.entry';
pdfjsLib.GlobalWorkerOptions.workerSrc =
  '//cdnjs.cloudflare.com/ajax/libs/pdf.js/3.0.279/pdf.worker.min.js';

const baseStyle = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '20px',
  borderWidth: 2,
  borderRadius: 2,
  borderColor: '#eeeeee',
  borderStyle: 'dashed',
  backgroundColor: '#F5F8FE',
  outline: 'none',
  transition: 'border .24s ease-in-out',
  cursor: 'pointer',
};

const focusedStyle = {
  borderColor: '#2196f3',
};

const acceptStyle = {
  borderColor: '#00e676',
};

const rejectStyle = {
  borderColor: '#ff1744',
};

const BulkUploadPdf = (props: any) => {
  const [files, setFiles] = useState<any>([]);
  const handleClose = () => {
    props.handleClose();
  };

  const convertCanvas = async (file: any) => {
    const canvasElement = document.getElementById(
      'pdf-canvas'
    ) as HTMLCanvasElement;
    const ctx = canvasElement.getContext('2d');
    const fileReader = new FileReader();

    fileReader.onload = async function (e) {
      const typedArray = new Uint8Array(e.target?.result as ArrayBuffer);
      const pdfDoc = await pdfjsLib.getDocument(typedArray).promise;

      // Render the first page (for example)
      const page = await pdfDoc.getPage(1);
      const viewport = page.getViewport({ scale: 1.5 });

      // Set canvas dimensions
      canvasElement.width = viewport.width;
      canvasElement.height = viewport.height;

      // Render the page into the canvas context
      const renderContext: any = {
        canvasContext: ctx,
        viewport: viewport,
      };

      await page.render(renderContext).promise;
    };

    fileReader.readAsArrayBuffer(file); // Read the file as an ArrayBuffer

    setTimeout(async () => {
      QrScanner.scanImage(canvasElement, {
        returnDetailedScanResult: true,
      })
        .then((result) => {
          try {
            const patient = JSON.parse(result.data);
            Object.assign(file, {
              preview: URL.createObjectURL(file),
              patientno: patient.patientno,
              fullname: patient.fullname,
              errorMessage: '',
            });
          } catch (error) {
            Object.assign(file, {
              preview: URL.createObjectURL(file),
              patientno: '',
              fullname: '',
              errorMessage:
                '患者情報が読み取れませんでした 患者画面から登録してください',
            });
          }
        })
        .catch((error) => {
          console.log(error || 'No QR code found.');
          Object.assign(file, {
            preview: URL.createObjectURL(file),
            patientno: '',
            fullname: '',
            errorMessage:
              '患者情報が読み取れませんでした 患者画面から登録してください',
          });
        })
        .finally(() => {
          setFiles((prevState: any) => [...prevState, file]);
          // if (props.allFile || (!props.allFile && file.errorMessage === '')) {
          //   setFiles((prevState: any) => [...prevState, file]);
          // }
        });
    }, 1000);
  };

  const onDrop = useCallback(async (acceptedFiles: any) => {
    const countFile = acceptedFiles.length;
    let eachCount = 0;
    let intervalId = setInterval(function () {
      convertCanvas(acceptedFiles[eachCount]);
      if (eachCount === countFile - 1) {
        clearInterval(intervalId);
      }
      eachCount++;
    }, 1500);
    // eslint-disable-next-line
  }, []);

  const { getRootProps, getInputProps, isFocused, isDragAccept, isDragReject } =
    useDropzone({
      onDrop,
      accept: {
        'application/pdf': ['.pdf'],
      },
    });

  const style: any = useMemo(
    () => ({
      ...baseStyle,
      ...(isFocused ? focusedStyle : {}),
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {}),
    }),
    [isFocused, isDragAccept, isDragReject]
  );

  const handleClearAll = () => {
    setFiles([]);
  };

  const handleSave = () => {
    console.log(files);
    const token = getToken();
    const formData = new FormData();
    formData.append('token', token);
    formData.append('type', 'doc');
    formData.append('patientid', props.patient?.id || '');
    files.forEach((file: any, index: number) => {
      console.log(file);
      console.log(file.name);
      formData.append(`files[${index}]`, file);
      formData.append(`customfilename[${index}]`, file.name);
      formData.append(`createdat[${index}]`, getToday());
    });

    try {
      postFormDataAPI(`/file/upload?token=${token}`, formData).then((res) => {
        if (res.code === 200) {
          console.log('upload success');
        }
      });
    } catch (error) {
      console.log(error);
    }
  };

  const handleRemoveFile = (index: number) => {
    let newFiles = [...files];
    newFiles.splice(index, 1);
    setFiles(newFiles);
  };

  return (
    <>
      <canvas id='pdf-canvas' style={{ width: '1px', height: '1px' }}></canvas>
      <Box sx={{ width: '100%' }}>
        <Box {...getRootProps({ style })}>
          <input {...getInputProps()} />
          <Stack spacing={'8px'} alignItems={'center'}>
            <Box
              component={'img'}
              src='/images/icons/ic-upload-file.svg'
              alt='upload-file'
              sx={{ cursor: 'pointer' }}
              width={'24px'}
            />
            <Typography fontSize={'14px'} fontWeight={500}>
              ここにファイルをドラッグ&ドロップ
            </Typography>
            <Typography fontSize={'10px'}>または</Typography>
            <Typography fontSize={'14px'} color={'#23B7E5'}>
              ファイルを選択する
            </Typography>
            <Typography fontSize={'10px'}>
              アップロードできるファイル形式はPDFのみです。
            </Typography>
          </Stack>
        </Box>
        {/* <aside>
    <ul>{files}</ul>
  </aside> */}
      </Box>
      <Worker workerUrl='https://unpkg.com/pdfjs-dist@3.4.120/build/pdf.worker.js'>
        {files.length > 0 && (
          <Grid container spacing={2} mt={'20px'}>
            {files.map((file: any, index: number) => {
              return (
                <Grid item xs={3} md={props.columnMd} key={index}>
                  <Stack spacing={'10px'}>
                    <Stack
                      direction={'row'}
                      justifyContent={'space-between'}
                      spacing={'8px'}
                    >
                      <Typography
                        fontSize={'10px'}
                        sx={{
                          wordBreak: 'break-word',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          lineClamp: 2,
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                        }}
                        height={'30px'}
                      >
                        {file.name}
                      </Typography>
                      <Box
                        component={'img'}
                        src='/images/icons/ic-trash.svg'
                        alt='upload-file'
                        sx={{ cursor: 'pointer' }}
                        width={'24px'}
                        onClick={() => handleRemoveFile(index)}
                      />
                    </Stack>
                    <Box
                      sx={{
                        cursor: 'pointer',
                        '& .rpv-core__viewer .rpv-core__inner-pages': {
                          overflow: 'hidden',
                        },
                        border: '1px solid #F3F3F3',
                      }}
                      width={'100%'}
                      height={'150px'}
                    >
                      <Viewer
                        fileUrl={file.preview}
                        defaultScale={SpecialZoomLevel.PageFit}
                      />
                    </Box>
                    <Stack
                      direction={'row'}
                      justifyContent={'space-between'}
                      spacing={'8px'}
                    >
                      {file.errorMessage ? (
                        <Typography color={'#DA4453'}>
                          {file.errorMessage}
                        </Typography>
                      ) : (
                        <>
                          <Typography>
                            {file.patientno || ''}
                          </Typography>
                          <Typography>
                            {file.fullname || ''}
                          </Typography>
                        </>
                      )}
                    </Stack>
                  </Stack>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Worker>
      <Stack
        direction={'row'}
        spacing={'10px'}
        justifyContent={'center'}
        alignItems={'center'}
        pt={'30px'}
      >
        <BtnOutlineGradient handleButtonClick={handleClose} text='キャンセル' />
        {files.length > 0 && (
          <BtnOutlineGradient
            handleButtonClick={handleClearAll}
            text='すべてクリア'
          />
        )}
        <BtnGradient handleButtonClick={handleSave} text='取込' />
      </Stack>
    </>
  );
};

export default BulkUploadPdf;
